import React, { useState, useEffect, useCallback } from 'react';
import BackOfficeLayout from '../../components/backoffice/BackOfficeLayout';
import { useAuth } from '../../context/AuthContext';
import { useEnvironment } from '../../context/EnvironmentContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  History, RefreshCw, User, Building2, Calendar,
  MessageSquare, CheckCircle, XCircle, AlertCircle, Clock,
  Shield, FileCheck, FileX, Send, ShieldCheck
} from 'lucide-react';
import { toast } from '../../lib/safeToast';

const ACTION_CONFIG = {
  co_review: {
    approve:      { badge: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'CO: Recommend Approve', icon: CheckCircle },
    reject:       { badge: 'bg-red-50 text-red-700 border-red-200',             label: 'CO: Recommend Reject',  icon: XCircle },
    request_info: { badge: 'bg-amber-50 text-amber-700 border-amber-200',       label: 'CO: Request Info',      icon: AlertCircle },
  },
  document_accepted:          { badge: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'Document Accepted',   icon: FileCheck },
  document_update_requested:  { badge: 'bg-amber-50 text-amber-700 border-amber-200',       label: 'Document Update Requested', icon: FileX },
  merchant_response:          { badge: 'bg-blue-50 text-blue-700 border-blue-200',           label: 'Merchant Responded',  icon: Send },
  su_approved:                { badge: 'bg-emerald-50 text-emerald-700 border-emerald-200',  label: 'SU: Approved',        icon: ShieldCheck },
  su_rejected:                { badge: 'bg-red-50 text-red-700 border-red-200',              label: 'SU: Rejected',        icon: XCircle },
};

function getActionMeta(row) {
  const at = row.action_type;
  if (at === 'co_review') {
    return ACTION_CONFIG.co_review[row.recommendation] || ACTION_CONFIG.co_review.request_info;
  }
  if (ACTION_CONFIG[at]) return ACTION_CONFIG[at];
  if (row.recommendation) {
    return ACTION_CONFIG.co_review[row.recommendation] || { badge: 'bg-slate-50 text-slate-600 border-slate-200', label: label(row.recommendation), icon: AlertCircle };
  }
  return { badge: 'bg-slate-50 text-slate-600 border-slate-200', label: 'Action', icon: Clock };
}

const DECISION_BADGE = {
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected: 'bg-red-50 text-red-700 border-red-200',
};

const FRIENDLY_LABELS = {
  co_recommended_approve: 'Compliance Approved',
  co_recommended_reject: 'Compliance Rejected',
  pending_su_approval: 'Awaiting SU Decision',
  pending_su_review: 'Awaiting SU Decision',
};
const label = (s) =>
  FRIENDLY_LABELS[s] || (s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

function formatDate(dateStr) {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('en-AU', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch { return '—'; }
}

function formatDateTime(dateStr) {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleString('en-AU', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return '—'; }
}

function truncate(str, len = 60) {
  if (!str) return '—';
  return str.length > len ? str.slice(0, len) + '…' : str;
}

function DetailRow({ icon: Icon, label: lbl, children }) {
  return (
    <div className="flex items-start gap-3 py-2.5">
      <div className="w-8 h-8 rounded-lg bg-[#E6F7F3] flex items-center justify-center shrink-0 mt-0.5">
        <Icon className="w-4 h-4 text-[#0B6445]" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-0.5">{lbl}</p>
        <div className="text-sm text-[#0F172A]">{children}</div>
      </div>
    </div>
  );
}

export default function BackOfficeReviewHistory() {
  const { api, user } = useAuth();
  const { isLive } = useEnvironment();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const isSU = user?.role === 'admin';

  const fetchReviews = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/backoffice/kyc/reviews');
      setRows(res.data?.data || []);
    } catch {
      setRows([]);
      toast.error('Failed to load review history');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews, isLive]);

  return (
    <BackOfficeLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between pb-4">
          <div>
            <h1 className="text-2xl font-bold text-[#073B2E] flex items-center gap-2">
              <History className="w-7 h-7 text-[#0B6445]" />
              Review History
            </h1>
            <p className="text-[#0E1B16]/70 mt-2">
              {isSU ? 'Complete timeline of all KYC review actions.' : 'Your KYC review history and actions.'}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchReviews}
            className="border-[#1C472B] text-[#1C472B] hover:bg-[#E6F7F3] hover:text-[#073B2E]"
          >
            <RefreshCw className="w-4 h-4 mr-1" />
            Refresh
          </Button>
        </div>

        <Card className="border-[#DCE4E0] shadow-sm">
          <CardContent className="pt-6">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="spinner w-8 h-8" />
              </div>
            ) : rows.length === 0 ? (
              <p className="text-center py-12 text-[#0E1B16]/60">No review actions found.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#DCE4E0]">
                      <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">Merchant</th>
                      <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">Action</th>
                      <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">By</th>
                      <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">Details</th>
                      <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, i) => {
                      const meta = getActionMeta(row);
                      const ActionIcon = meta.icon;
                      return (
                        <tr
                          key={row.id || i}
                          className="border-b border-[#DCE4E0]/80 hover:bg-[#F0FAF6] cursor-pointer transition-colors"
                          onClick={() => setSelected(row)}
                        >
                          <td className="py-3 px-2">
                            <div>
                              <p className="font-medium text-[#073B2E]">{row.merchant_name || '—'}</p>
                              {row.company_name && (
                                <p className="text-xs text-slate-500">{row.company_name}</p>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-2">
                            <Badge variant="outline" className={`${meta.badge} text-xs`}>
                              <ActionIcon className="w-3 h-3 mr-1" />
                              {meta.label}
                            </Badge>
                          </td>
                          <td className="py-3 px-2 text-slate-600">{row.reviewer_name || '—'}</td>
                          <td className="py-3 px-2 max-w-[250px] text-slate-600" title={row.comments || ''}>
                            {truncate(row.comments, 50)}
                          </td>
                          <td className="py-3 px-2 whitespace-nowrap text-slate-500">
                            {formatDate(row.created_at)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(open) => { if (!open) setSelected(null); }}>
        <DialogContent className="sm:max-w-[640px] max-h-[85vh] overflow-y-auto p-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-[#DCE4E0]">
            <DialogTitle className="text-lg font-bold text-[#073B2E]">
              Review Action Details
            </DialogTitle>
            {selected && (() => {
              const meta = getActionMeta(selected);
              const ActionIcon = meta.icon;
              return (
                <div className="flex items-center gap-3 mt-3">
                  <Badge variant="outline" className={`${meta.badge} text-sm px-3 py-1`}>
                    <ActionIcon className="w-3.5 h-3.5 mr-1.5" />
                    {meta.label}
                  </Badge>
                  <span className="text-xs text-slate-500">{formatDateTime(selected.created_at)}</span>
                </div>
              );
            })()}
          </DialogHeader>

          {selected && (
            <div className="px-6 py-5 space-y-1">
              {/* Merchant */}
              <DetailRow icon={User} label="Merchant">
                <p className="font-medium">{selected.merchant_name || '—'}</p>
                {selected.merchant_email && (
                  <p className="text-xs text-slate-500 mt-0.5">{selected.merchant_email}</p>
                )}
              </DetailRow>

              {selected.company_name && (
                <DetailRow icon={Building2} label="Company">
                  {selected.company_name}
                </DetailRow>
              )}

              {/* Actor */}
              <DetailRow icon={Shield} label="Action By">
                <p className="font-medium">{selected.reviewer_name || '—'}</p>
                <p className="text-xs text-slate-500 mt-0.5">{label(selected.reviewer_role || '')}</p>
              </DetailRow>

              <DetailRow icon={Calendar} label="Date">
                {formatDateTime(selected.created_at)}
              </DetailRow>

              {/* Divider */}
              <div className="border-t border-[#DCE4E0] my-4" />

              {/* Action type description */}
              {(() => {
                const meta = getActionMeta(selected);
                const ActionIcon = meta.icon;
                return (
                  <DetailRow icon={ActionIcon} label="Action">
                    <Badge variant="outline" className={`${meta.badge} text-sm px-3 py-1`}>
                      {meta.label}
                    </Badge>
                  </DetailRow>
                );
              })()}

              {/* Document type (for document actions) */}
              {selected.doc_type && (
                <DetailRow icon={FileCheck} label="Document">
                  {label(selected.doc_type)}
                </DetailRow>
              )}

              {/* Comments / Details */}
              <DetailRow icon={MessageSquare} label={selected.action_type === 'merchant_response' ? 'Merchant Response' : 'Comments'}>
                <p className="whitespace-pre-wrap text-slate-700 leading-relaxed">
                  {selected.comments || '—'}
                </p>
              </DetailRow>

              {/* SU Decision (for co_review actions) */}
              {selected.action_type === 'co_review' && selected.su_decision && (
                <>
                  <div className="border-t border-[#DCE4E0] my-4" />
                  <div className={`rounded-lg border p-4 ${
                    selected.su_decision === 'approved'
                      ? 'bg-emerald-50/60 border-emerald-200'
                      : 'bg-red-50/60 border-red-200'
                  }`}>
                    <div className="flex items-center gap-2 mb-2">
                      {selected.su_decision === 'approved'
                        ? <CheckCircle className="w-4 h-4 text-emerald-600" />
                        : <XCircle className="w-4 h-4 text-red-600" />
                      }
                      <p className={`text-sm font-semibold ${
                        selected.su_decision === 'approved' ? 'text-emerald-800' : 'text-red-800'
                      }`}>
                        Super Admin Decision: {label(selected.su_decision)}
                      </p>
                    </div>
                    {selected.su_comments && (
                      <p className={`text-sm leading-relaxed ${
                        selected.su_decision === 'approved' ? 'text-emerald-700' : 'text-red-700'
                      }`}>
                        {selected.su_comments}
                      </p>
                    )}
                  </div>
                </>
              )}

              {/* Pending SU for co_review approve/reject recommendations */}
              {selected.action_type === 'co_review' && !selected.su_decision && selected.recommendation !== 'request_info' && (
                <>
                  <div className="border-t border-[#DCE4E0] my-4" />
                  <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 flex items-center gap-3">
                    <Clock className="w-5 h-5 text-slate-400" />
                    <p className="text-sm text-slate-500">Awaiting Super Admin decision…</p>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </BackOfficeLayout>
  );
}
